import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import {
  ActionEvent,
  FremdleistungStackViewComponent,
} from './fremdleistung-stack-view/fremdleistung-stack-view.component';
import {
  KostenEditModalComponent,
  MaschineEditDataWithImage,
  MaterialEditDataWithImage,
  PersonalEditDataWithImage,
} from './kosten-edit-modal/kosten-edit-modal.component';
import { KostenFremdleistungModalComponent } from './kosten-fremdleistung-modal/kosten-fremdleistung-modal.component';
import { ClarityModule, ClrDatagrid } from '@clr/angular';
import { CommonModule } from '@angular/common';
import { Column, Row } from 'src/app/models/ui/table';
import { rxResource } from '@angular/core/rxjs-interop';
import { PartialStateUpdater, patchState, signalState } from '@ngrx/signals';
import { ConfirmService } from 'src/app/services/confirm.service';
import { difference } from 'src/app/misc/functions';

//#region Interfaces
const enum KostenTyp {
  PERSONAL = 1,
  MATERIAL = 2,
  MASCHINE = 3,
  FREMDLEISTUNG = 4,
}

interface ActionEventInternal {
  action: 'add' | 'edit' | 'delete';
  data: any;
}

interface FremdleistungAddData {
  ID: number;
  Titel_ID: number;
  Hauptgruppe: number;
}

interface FremdleistungEditData extends FremdleistungAddData {
  ID: number;
  OZ?: string;
  Kurztext: string;
  Langtext: string;
  ME: string;
  EP: number;
}

export interface KostenSummary {
  rows: {
    personal: Row[];
    material: Row[];
    maschine: Row[];
    fremdleistung: Row[];
  };
  columns: Column[];
  summary: {
    kosten: string;
    plankosten: string;
  };
}

type KostenMode = keyof KostenSummary['rows'];
//#endregion

@Component({
  selector: 'app-kostenerfassung',
  templateUrl: './kostenerfassung.component.html',
  styleUrls: ['./kostenerfassung.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    KostenEditModalComponent,
    KostenFremdleistungModalComponent,
    FremdleistungStackViewComponent,
    TablePrettyPrintPipe,
    HideIDColumnsPipe,
    MrTranslatePipe,
    BackendLocaleDatePipe,
  ],
})
export class KostenerfassungComponent implements OnInit, OnDestroy {
  private readonly apiservice = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly changeDet = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  public readonly refreshGesamtkosten = output<void>();
  public readonly getPlankostenEvent = output<KostenSummary>();
  public readonly action = output<ActionEventInternal>();

  public readonly isPlankosten = input(false);
  public readonly inforClosed = input(false);
  public readonly inforJobNrs = input<string[]>([]);
  public readonly inforObjCount = input<number>(0);
  public readonly kauftrid = input<number>(-1);
  public readonly auftrag = input<Row>(undefined);

  protected readonly multiObj = computed(() => this.inforJobNrs().length > 1 || this.inforObjCount() > 1);
  protected readonly isMaterialLoading = signal(true);
  protected readonly noActivitiesSentinel = '__MRPRO_NO_ACTIVITIES__';

  private readonly optInterface = +this.authService.decrypt(localStorage.getItem('optInterface'));
  protected readonly isHxGN_Interface = this.optInterface === 3;

  private readonly mehResource = rxResource({
    params: () => !this.inforClosed() || undefined,
    stream: () => this.apiservice.getKostenFremdleistungEinheiten(),
  });
  protected readonly meh = computed(() => this.mehResource.value() ?? []);

  protected readonly kostenTable = signalState<KostenSummary>({
    rows: {
      personal: [],
      material: [],
      maschine: [],
      fremdleistung: [],
    },
    columns: null,
    summary: {
      kosten: '0.00',
      plankosten: '0.00',
    },
  });
  protected readonly rowsCount = computed(() => kostenAllRows(this.kostenTable()).length);

  // --- PERSONAL ---

  personalForm = new UntypedFormGroup({
    datum: new UntypedFormControl(BackendLocaleDatePipe.now, [Validators.required]),
    arbeitszeitStart: new UntypedFormControl('07:00', [Validators.required]),
    arbeitszeitEnde: new UntypedFormControl('16:00', [Validators.required]),
    arbeitszeit: new UntypedFormControl(''),
    taetigkeit: new UntypedFormControl('', [Validators.required]),
    bemerkung: new UntypedFormControl(''),
  });

  @ViewChild('kostenDg') kostenDg: ClrDatagrid;
  @ViewChildren('datagrid') datagrids: ClrDatagrid[];

  personalTable = {
    rows: null,
    columns: [],
  };

  // --- MATERIAL ---

  materialForm = new UntypedFormGroup({
    datum: new UntypedFormControl(BackendLocaleDatePipe.now, [Validators.required]),
    anzahl: new UntypedFormControl('', [Validators.required]),
    bemerkung: new UntypedFormControl(''),
  });

  materialTable = {
    rows: null,
    columns: [],
  };

  // --- MASCHINEN ---

  maschinenForm = new UntypedFormGroup({
    datum: new UntypedFormControl(BackendLocaleDatePipe.now, [Validators.required]),
    arbeitszeitStart: new UntypedFormControl('07:00', [Validators.required]),
    arbeitszeitEnde: new UntypedFormControl('16:00', [Validators.required]),
    arbeitszeit: new UntypedFormControl(''),
    taetigkeit: new UntypedFormControl('', [Validators.required]),
    bemerkung: new UntypedFormControl(''),
  });

  maschinenTable = {
    rows: null,
    columns: [],
  };

  // --- FREMDLEISTUNG ---

  fremdleistungForm = new UntypedFormGroup({
    datum: new UntypedFormControl(BackendLocaleDatePipe.now, [Validators.required]),
    kurztext: new UntypedFormControl(''),
    anzahl: new UntypedFormControl('', [Validators.required]),
    einheit: new UntypedFormControl(''),
    bemerkung: new UntypedFormControl(''),
  });

  fremdLeistungTitles: any[] = [];
  fremdleistungen: any[] = [];

  selected = {
    personal: null,
    material: null,
    maschine: null,
    fremdleistung: null,
  };

  $deleteSelection: BehaviorSubject<void> = new BehaviorSubject(null);

  modals: {
    personal: boolean;
    material: boolean;
    maschine: boolean;
    fremdleistung: boolean | string;
  } = {
    personal: false,
    material: false,
    maschine: false,
    fremdleistung: false,
  };

  modalData: {
    personal: PersonalEditDataWithImage | null;
    material: MaterialEditDataWithImage | null;
    maschine: MaschineEditDataWithImage | null;
    fremdleistung: FremdleistungAddData | FremdleistungEditData | null;
  } = {
    personal: null,
    material: null,
    maschine: null,
    fremdleistung: null,
  };

  zeiten = {
    personal: null,
    maschine: null,
  };

  taetigkeiten = { personal: [], maschine: [] };

  subscriptions: Subscription[] = [];

  trial = false;
  selectedpersonal: any = [];
  username: string;

  constructor() {
    this.trial = this.authService.getToken().trial;
    effect(() => {
      const status = +this.auftrag()?.Auftragsstatus;
      if (status == 1 || status == 2) {
        this.personalForm.disable();
        this.materialForm.disable();
        this.maschinenForm.disable();
        this.fremdleistungForm.disable();
      }
    });
  }

  ngOnInit(): void {
    this.username = sessionStorage.getItem('UserName');

    if (this.kostenTable.columns() === null) {
      this.loadAuftragKosten();
    }

    if (!this.inforClosed()) {
      this.apiservice
        .getKostenTaetigkeiten()
        .pipe(first())
        .subscribe((res: any) => {
          if (res) this.taetigkeiten = res;
        });

      this.loadPersonal();

      this.loadMaterial();

      this.loadMaschine();

      this.loadFremdleistung();
    }

    this.subscriptions.push(
      this.personalForm.controls.arbeitszeitStart.valueChanges.subscribe(_ => this.calculateZeiten('personal'))
    );
    this.subscriptions.push(
      this.personalForm.controls.arbeitszeitEnde.valueChanges.subscribe(_ => this.calculateZeiten('personal'))
    );

    this.subscriptions.push(
      this.maschinenForm.controls.arbeitszeitStart.valueChanges.subscribe(_ => this.calculateZeiten('maschine'))
    );
    this.subscriptions.push(
      this.maschinenForm.controls.arbeitszeitEnde.valueChanges.subscribe(_ => this.calculateZeiten('maschine'))
    );
  }

  doAction(action: 'add' | 'edit' | 'delete') {
    this.action.emit({ action, data: this.selected });
  }

  importHxGNEAM(type: string) {
    this.apiservice
      .importHxGNEAMKosten(type)
      .pipe(first())
      .subscribe((res: any) => {
        if (res.success) {
          switch (type) {
            case 'personal':
              this.loadPersonal();
              break;
            case 'material':
              this.loadMaterial();
              break;
            case 'maschine':
              this.loadMaschine();
              break;
            case 'fremdleistung':
              this.loadFremdleistung();
              break;
          }
          this.toastr.success(this.mrTranslate.transform('Daten wurden erfolgreich importiert'));
        } else {
          this.toastr.warning(this.mrTranslate.transform(res.message));
        }
      });
  }

  private loadMaschine() {
    this.maschinenTable = {
      rows: null,
      columns: [],
    };
    this.changeDet.detectChanges();
    this.apiservice
      .getKostenMaschinen()
      .pipe(first())
      .subscribe((res: any) => {
        if (res)
          this.maschinenTable = {
            columns: res.columns,
            rows: res.rows && res.rows.length > 0 ? [...res.rows] : [],
          };
        this.modals.maschine = false;
      });
  }

  private loadMaterial() {
    this.materialTable = {
      rows: null,
      columns: [],
    };
    this.changeDet.detectChanges();
    this.apiservice
      .getKostenMaterial(this.optInterface.toString())
      .pipe(first())
      .subscribe((res: any) => {
        if (res) {
          if (this.isHxGN_Interface) {
            let rows = [];
            if (res.dt.rows && res.dt.rows.length > 0) {
              rows = res.dt.rows.filter(items => items.Lagerbestand > 0);
            }
            this.materialTable = {
              columns: res.dt.columns,
              rows: [...rows],
            };
          } else {
            this.materialTable = {
              columns: res.dt.columns,
              rows: res.dt.rows && res.dt.rows.length > 0 ? [...res.dt.rows] : [],
            };
          }

          if (res.message) {
            this.toastr.warning(this.mrTranslate.transform(res.message));
          }
        }
        this.isMaterialLoading.set(false);

        this.modals.material = false;
      });
  }

  private loadPersonal(initial = true) {
    this.personalTable = {
      rows: null,
      columns: [],
    };
    this.changeDet.detectChanges();
    this.apiservice
      .getKostenPersonal()
      .pipe(first())
      .subscribe((res: any) => {
        if (res) {
          this.personalTable = {
            columns: res.columns,
            rows: res.rows && res.rows.length > 0 ? [...res.rows] : [],
          };
          this.modals.personal = false;
        }
      });
  }

  private loadFremdleistung() {
    this.saveFremdleistungExpanded();

    this.fremdleistungen = [];
    this.fremdLeistungTitles = [];
    this.changeDet.detectChanges();
    this.apiservice
      .getKostenFremdleistung()
      .pipe(first())
      .subscribe((res: any) => {
        const expanded = JSON.parse(localStorage.getItem('fremdleistungenExpanded'));
        if (res && !res.error) {
          const titel =
            res.titel?.map(t => ({
              ...t,
              Kurztext: t.Bezeichnung,
              OZ: '',
              children: [],
              expanded: expanded?.includes(t.ID) ?? false,
              Titel_ID: 0,
            })) ?? [];

          const input = res.leistungen?.map(l => ({ ...l, selected: 0, expanded: false })) ?? [];

          const hauptNodes = input
            .filter(l => l.Hauptgruppe === '-' || l.Hauptgruppe === '')
            .map(l => ({ ...l, expanded: true }));
          let childNodes = input.filter(l => l.Hauptgruppe !== '-' && l.Hauptgruppe !== '');

          // add childLeistungen to leistungen matching on +Hauptgruppe == ID
          // add children recursively since there can be multiple levels
          const addChildren = leistung => {
            const children = childNodes.filter(l => +l.Hauptgruppe === leistung.ID);
            // remove children from childLeistungen
            childNodes = difference(childNodes, children);

            if (children.length > 0) {
              leistung.children = children;
              children.forEach(child => addChildren(child));
            }
          };

          hauptNodes.forEach(leistung => addChildren(leistung));
          hauptNodes.forEach(leistung => {
            const t = titel.find(t => t.ID === leistung.Titel_ID);
            if (t) t.children.push(leistung);
          });

          this.fremdleistungen = titel;
          this.modals.fremdleistung = false;
        }
      });
  }

  private saveFremdleistungExpanded() {
    if (this.fremdleistungen.length > 0) {
      // save expanded state of fremdleistungen
      const expanded = this.fremdleistungen.filter(t => t.expanded).map(t => t.ID);
      localStorage.setItem('fremdleistungenExpanded', JSON.stringify(expanded));
    }
  }

  private async loadAuftragKosten() {
    const { rows, columns } = await firstValueFrom(this.apiservice.getKostenAuftrag(this.kauftrid()));
    const personal: Row[] = [];
    const material: Row[] = [];
    const maschine: Row[] = [];
    const fremdleistung: Row[] = [];

    let kosten = 0;
    let plankosten = 0;

    for (const row of rows) {
      kosten += +row.Kosten;
      plankosten += +row.PlanKosten;

      switch (row.KostenTyp) {
        case KostenTyp.PERSONAL:
          personal.push(row);
          break;
        case KostenTyp.MATERIAL:
          material.push(row);
          break;
        case KostenTyp.MASCHINE:
          maschine.push(row);
          break;
        case KostenTyp.FREMDLEISTUNG:
          fremdleistung.push(row);
          break;
      }
    }

    patchState(this.kostenTable, {
      columns,
      rows: {
        personal,
        material,
        maschine,
        fremdleistung,
      },
      summary: {
        kosten: kosten.toFixed(2),
        plankosten: plankosten.toFixed(2),
      },
    });
  }

  calculateZeiten(type: 'personal' | 'maschine') {
    const form = this.getForm(type);
    const start = form.controls.arbeitszeitStart.value;
    const ende = form.controls.arbeitszeitEnde.value;
    if (start && ende) {
      let diff = dayjs(`2000-01-01 ${ende}`).diff(dayjs(`2000-01-01 ${start}`), 'hours', true);
      if (diff < 0) diff = 0;
      const isFloat = diff % 1 !== 0;
      this.zeiten = {
        ...this.zeiten,
        [type]: isFloat ? diff.toFixed(2).replace('.', ',') : diff.toString(),
      };
    }
  }

  private getForm(type: string) {
    let form = null;
    switch (type) {
      case 'personal':
        form = this.personalForm;
        break;
      case 'material':
        form = this.materialForm;
        break;
      case 'maschine':
        form = this.maschinenForm;
        break;
      case 'fremdleistung':
        form = this.fremdleistungForm;
        break;
    }
    return form;
  }

  deleteItem(mode: KostenMode, data: Row) {
    let rows: Row[];
    switch (mode) {
      case 'personal':
        rows = this.kostenTable.rows.personal();
        break;
      case 'material':
        rows = this.kostenTable.rows.material();
        break;
      case 'maschine':
        rows = this.kostenTable.rows.maschine();
        break;
      case 'fremdleistung':
        rows = this.kostenTable.rows.fremdleistung();
        break;
    }

    const index = rows.indexOf(data);
    if (index != -1) {
      patchState(this.kostenTable, kostenRowsUpdater(mode, rows.splice(index, 1)));
      this.getPlankostenEvent.emit(this.kostenTable());
    }
  }

  openModal(type: string, data: any) {
    const mdData = { ...this.modalData };
    if (data) {
      switch (type) {
        case 'personal':
          mdData.personal = {
            id: data.ID,
            personalnummer: data.PersonalNummer,
            kuerzel: data.Kuerzel,
            vorname: data.Vorname,
            nachname: data.Nachname,
            email: data.Email,
            qualifikation: data.Qualifikation,
            wochenarbeitszeit: data.Wochenstunden,
            verrechnungssatz: data.Lohn,
            bild: data.Bild,
          };
          break;
        case 'material':
          mdData.material = {
            id: data.ID,
            materialnummer: data.MaterialNummer,
            titel: data.Titel,
            beschreibung: data.Beschreibung,
            einheit: data.Einheit,
            einzelpreis: data.Preis,
            bild: data.Bild,
          };
          break;
        case 'maschine':
          mdData.maschine = {
            id: data.ID,
            maschinennummer: data.MaschinenNummer,
            titel: data.Titel,
            beschreibung: data.Beschreibung,
            verrechnungssatz: data.Preis,
            bild: data.Bild,
          };
          break;
        case 'fremdleistung':
          mdData.fremdleistung = null;
          break;
      }
      this.modalData = { ...mdData };
    } else {
      switch (type) {
        case 'personal':
          mdData.personal = {
            id: -99,
            personalnummer: '',
            kuerzel: '',
            vorname: '',
            nachname: '',
            email: '',
            qualifikation: '',
            wochenarbeitszeit: 0,
            verrechnungssatz: 0,
            bild: '',
          };
          break;
        case 'material':
          mdData.material = {
            id: -99,
            materialnummer: '',
            titel: '',
            beschreibung: '',
            einheit: '',
            einzelpreis: 0,
            bild: '',
          };
          break;
        case 'maschine':
          mdData.maschine = {
            id: -99,
            maschinennummer: '',
            titel: '',
            beschreibung: '',
            verrechnungssatz: 0,
            bild: '',
          };
          break;
        case 'fremdleistung':
          mdData.fremdleistung = null;
          break;
      }
    }
    this.modals[type] = true;
  }

  closeModal(type: string, reload: any) {
    if (reload) {
      setTimeout(() => {
        switch (type) {
          case 'personal':
            this.loadPersonal();
            break;
          case 'material':
            this.loadMaterial();
            break;
          case 'maschine':
            this.loadMaschine();
            break;
          case 'fremdleistung':
            this.loadFremdleistung();
            break;
        }
      });
    } else {
      this.modals = { ...this.modals, [type]: false };
    }
  }

  saveFremdleistung() {
    let type = 'fremdleistung';
    let form: UntypedFormGroup = this.getForm(type);

    const selected = this.selected[type];
    if (selected === null || (type === 'personal' && selected.length === 0)) {
      const typeName = type[0].toUpperCase() + type.slice(1);
      const text = this.mrTranslate.transform('Keine Fremdleistung ausgewählt');
      this.toastr.warning(text);
      return;
    }

    if (!form.valid) {
      const text = this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen');
      this.toastr.warning(text);
      return;
    }

    let toSend = [
      {
        ...form.value,
        kostenID: selected.ID,
        auftragID: this.kauftrid(),
        inforjobnum: this.inforJobNrs(),
        optInterface: this.optInterface,
      },
    ];

    this.apiservice
      .setFremdleistungPlanenAuftrag(toSend)
      .pipe(first())
      .subscribe((res: any) => {
        if (res?.status == 'success') {
          const text = this.mrTranslate.transform('Fremdleistung erfolgreich gespeichert');
          this.toastr.success(text);
          form.reset();
          const newSelect = { ...this.selected };
          newSelect[type] = type === 'personal' ? [] : null;
          this.selected = newSelect;
          this.$deleteSelection.next();
          this.refreshGesamtkosten.emit();
          this.loadAuftragKosten();
        } else {
          this.toastr.error(this.mrTranslate.transform(res.message));
        }
      });
  }

  saveKosten(type: 'personal' | 'material' | 'maschine' | 'fremdleistung') {
    let form: UntypedFormGroup = this.getForm(type);

    if (this.isPlankosten()) {
      let vals = form.value;
      switch (type) {
        case 'personal':
          if (this.selectedpersonal.length == 0) {
            this.toastr.warning(this.mrTranslate.transform('Kein Personal ausgewählt'));
            return;
          }
          if (vals.datum == null || vals.datum == '' || vals.arbeitszeit == '' || vals.taetigkeit == '') {
            this.toastr.warning(this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen'));
            return;
          }

          this.selectedpersonal.forEach(obj => {
            let newrow = {
              ID: -1,
              Datum: vals.datum,
              Beschreibung: vals.taetigkeit,
              Umfang: 0,
              Einheit: 'Std',
              Kosten: 0,
              Bemerkung: vals.bemerkung,
              Beginn: '',
              Ende: '',
              Anzahl: vals.arbeitszeit,
              PlanKosten: (obj.Lohn * vals.arbeitszeit).toFixed(2),
              UserName: this.username,
              Status: BackendLocaleDatePipe.now,
              KostenTyp: 1,
              KostenID: obj.ID,
            };

            patchState(this.kostenTable, kostenRowsPusher('personal', newrow));
            this.selectedpersonal = [];
          });

          break;

        case 'material':
          const selected = this.selected[type];
          if (selected === null || selected.length === 0) {
            this.toastr.warning(this.mrTranslate.transform('Kein Material ausgewählt'));
            return;
          }

          if (!form.valid) {
            this.toastr.warning(this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen'));
            return;
          }

          let newrow = {
            ID: -1,
            Datum: vals.datum,
            Beschreibung: selected.Titel,
            Umfang: 0,
            Einheit: selected.Einheit,
            Kosten: 0,
            Bemerkung: vals.bemerkung,
            Beginn: '',
            Ende: '',
            Anzahl: vals.anzahl,
            PlanKosten: (selected.Preis * vals.anzahl).toFixed(2),
            UserName: this.username,
            Status: BackendLocaleDatePipe.now,
            KostenTyp: 2,
            KostenID: selected.ID,
          };

          patchState(this.kostenTable, kostenRowsPusher('material', newrow));
          this.selected[type] = null;
          break;

        case 'maschine':
          const selected1 = this.selected[type];
          if (selected1 === null || selected1.length === 0) {
            this.toastr.warning(this.mrTranslate.transform('Keine Maschine ausgewählt'));
            return;
          }

          if (vals.datum == null || vals.datum == '' || vals.arbeitszeit == '' || vals.taetigkeit == '') {
            this.toastr.warning(this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen'));
            return;
          }

          let newrow1 = {
            ID: -1,
            Datum: vals.datum,
            Beschreibung: selected1.Titel,
            Umfang: 0,
            Einheit: 'Std',
            Kosten: 0,
            Bemerkung: vals.bemerkung,
            Beginn: '',
            Ende: '',
            Anzahl: vals.arbeitszeit,
            PlanKosten: (selected1.Preis * vals.arbeitszeit).toFixed(2),
            UserName: this.username,
            Status: BackendLocaleDatePipe.now,
            KostenTyp: 3,
            KostenID: selected1.ID,
          };

          patchState(this.kostenTable, kostenRowsPusher('maschine', newrow1));
          this.selected[type] = null;
          break;

        case 'fremdleistung':
          const selected2 = this.selected[type];
          if (selected2 === null || selected2.length === 0) {
            this.toastr.warning(this.mrTranslate.transform('Keine Fremdleistung ausgewählt'));
            return;
          }

          if (!form.valid) {
            this.toastr.warning(this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen'));
            return;
          }
          let newrow3 = {
            ID: -1,
            Datum: vals.datum,
            Beschreibung: vals.kurztext,
            Umfang: 0,
            Einheit: vals.einheit,
            Kosten: 0,
            Bemerkung: '',
            Beginn: '',
            Ende: '',
            Anzahl: vals.anzahl,
            PlanKosten: (selected2.EP * vals.anzahl).toFixed(2),
            UserName: this.username,
            Status: BackendLocaleDatePipe.now,
            KostenTyp: 4,
            KostenID: selected2.ID,
          };

          patchState(this.kostenTable, kostenRowsPusher('fremdleistung', newrow3));
          this.selected[type] = null;
          break;
      }

      patchState(this.kostenTable, kostenSummaryUpdater);

      this.getPlankostenEvent.emit(this.kostenTable());

      return;
    }

    if (type === 'personal') {
      if (this.selectedpersonal.length == 0) {
        this.toastr.warning(this.mrTranslate.transform('Kein Personal ausgewählt'));
        return;
      }
      this.selected[type] = this.selectedpersonal[0];
    }

    let selected = this.selected[type];
    if (selected === null || (type === 'personal' && selected.length === 0)) {
      const typeName = type[0].toUpperCase() + type.slice(1);
      const text = this.mrTranslate
        .transform('Kein {0} ausgewählt')
        .replace('{0}', this.mrTranslate.transform(typeName));
      this.toastr.warning(text);
      return;
    }

    if (!form.valid) {
      const text = this.mrTranslate.transform('Bitte alle Pflichtfelder(*) ausfüllen');
      this.toastr.warning(text);
      return;
    }

    if (this.optInterface.toString() == '3' && type == 'fremdleistung' && !this.isPlankosten()) {
      if (!this.inforJobNrs().length) {
        const text =
          this.mrTranslate.transform('Buchung ist nicht möglich.') +
          '\n' +
          this.mrTranslate.transform('Es wurde kein INFOR JOBNUM gefunden!');
        this.toastr.warning(text);
        return;
      }
      const foundfremdleistung = this.kostenTable.rows.fremdleistung().find(u => u.KostenID === selected.ID);
      if (!foundfremdleistung) {
        const text =
          this.mrTranslate.transform('Buchung ist nicht zulässig.') +
          '\n' +
          this.mrTranslate.transform('Fremdleistung ist nicht im Auftrag enthalten!');
        this.toastr.warning(text);
        return;
      } else {
        let vals = form.value;
        this.getBestellungHxGNEAM(form, selected, foundfremdleistung);
        return;
      }
    }

    if (this.optInterface.toString() == '3' && type === 'material') {
      let vals = form.value;
      if (vals.anzahl > selected.Lagerbestand) {
        const text =
          this.mrTranslate.transform('Buchung ist nicht zulässig.') +
          '\n' +
          this.mrTranslate.transform('Anzahl ist größer als Lagerbestand!');
        this.toastr.warning(text);
        return;
      }
    }

    let toSend = [
      {
        ...form.value,
        kostenID: selected.ID,
        auftragID: this.kauftrid(),
        inforjobnum: this.inforJobNrs(),
        optInterface: this.optInterface,
      },
    ];

    if (type === 'personal') {
      let vals = form.value;
      let person = [];
      let id = 0;

      this.selectedpersonal.forEach(obj => {
        let newrow = {
          datum: vals.datum,
          arbeitszeitStart: vals.arbeitszeitStart,
          arbeitszeitEnde: vals.arbeitszeitEnde,
          arbeitszeit: vals.arbeitszeit,
          taetigkeit: vals.taetigkeit,
          bemerkung: vals.bemerkung,
        };

        let items = {
          ...newrow,
          kostenID: obj.ID,
          auftragID: this.kauftrid(),
          inforjobnum: this.inforJobNrs(),
          optInterface: this.optInterface,
        };
        person.push(items);
      });

      toSend = person;
    }

    this.saveToDatabase(form, type, toSend);
  }

  getBestellungHxGNEAM(form: UntypedFormGroup, selected: any, fremdleistung: any) {
    let vals = form.value;
    let send = {
      jobnum: this.inforJobNrs()[0],
      task: fremdleistung.Task,
    };

    this.apiservice
      .getHxGNEAMBestellung(send)
      .pipe(first())
      .subscribe((res: any) => {
        if (res.success) {
          let toSend = [
            {
              ...form.value,
              kostenID: selected.ID,
              auftragID: this.kauftrid(),
              inforjobnum: this.inforJobNrs(),
              optInterface: this.optInterface,
              bestellnummer: res.bestellnummer,
              bestelllinie: res.bestelllinie,
              activitycode: fremdleistung.Task,
            },
          ];
          this.saveToDatabase(form, 'fremdleistung', toSend);
        } else {
          this.toastr.error(this.mrTranslate.transform(res.message));
        }
      });
  }

  saveToDatabase(form: UntypedFormGroup, type: string, toSend: any) {
    this.apiservice
      .setKostenAuftrag(type, toSend)
      .pipe(first())
      .subscribe((res: any) => {
        if (res?.status == 'success') {
          const text = this.mrTranslate.transform('Kosten erfolgreich gespeichert');
          this.toastr.success(text);
          form.reset();
          const newSelect = { ...this.selected };
          newSelect[type] = type === 'personal' ? [] : null;
          this.selected = newSelect;
          this.$deleteSelection.next();
          this.refreshGesamtkosten.emit();

          if (type === 'personal') {
            this.selectedpersonal = [];
          }

          this.loadAuftragKosten();
        } else {
          this.toastr.error(this.mrTranslate.transform(res.message));
        }
      });
  }

  fremdleistungSelected(leistung: any) {
    if (leistung.EP > 0) {
      this.fremdleistungForm.controls.kurztext.setValue(leistung.Kurztext ?? '');
      this.fremdleistungForm.controls.einheit.setValue(leistung.ME ?? '');
      this.fremdleistungForm.controls.bemerkung.setValue(leistung.Langtext ?? '');
    }

    this.selected.fremdleistung = leistung;
  }

  fremdleistungAction(actionEvent: ActionEvent) {
    const action = actionEvent.action;
    const leistung = actionEvent.data;

    switch (action) {
      case 'add':
      case 'edit':
      case 'addLV':
      case 'editLV':
        this.modalData = {
          ...this.modalData,
          fremdleistung:
            leistung ?
              {
                ...leistung,
                Hauptgruppe: parseInt(leistung.Hauptgruppe),
              }
            : null,
        };
        this.modals = { ...this.modals, fremdleistung: action };
        break;
      case 'delete':
        this.deleteFremdleistung(leistung, 'fremdleistungen');
        break;
    }
  }

  async deleteFremdleistung(leistung: any, type: string) {
    const message = this.mrTranslate.transform('Sind Sie sicher, dass Sie diesen Eintrag löschen wollen?');
    const ok = await this.confirmService.danger(message);
    if (ok) {
      this.apiservice
        .deleteKostenFremdleistung(leistung.ID, type)
        .pipe(first())
        .subscribe((res: any) => {
          if (res?.status == 'success') {
            const text = this.mrTranslate.transform(type + ' erfolgreich gelöscht');
            this.toastr.success(text);
            switch (type) {
              case 'personal':
                this.loadPersonal();
                break;
              case 'material':
                this.loadMaterial();
                break;
              case 'maschine':
                this.loadMaschine();
                break;
              case 'fremdleistungen':
                this.loadFremdleistung();
                break;
            }
          } else {
            const text = this.mrTranslate.transform('Fremdleistung konnte nicht gelöscht werden');
            this.toastr.error(text, res.message ?? '');
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.saveFremdleistungExpanded();
  }
}

function kostenRowsUpdater(mode: KostenMode, newRows: Row[]): PartialStateUpdater<KostenSummary> {
  return state => ({ ...state, rows: { ...state.rows, [mode]: newRows } });
}

function kostenRowsPusher(mode: KostenMode, newRow: Row): PartialStateUpdater<KostenSummary> {
  return state => ({ ...state, rows: { ...state.rows, [mode]: [...state.rows[mode], newRow] } });
}

const kostenSummaryUpdater: PartialStateUpdater<KostenSummary> = state => {
  const allRows = kostenAllRows(state);
  const { kosten, plankosten } = allRows.reduce<{ kosten: number; plankosten: number }>(
    (summary, row) => {
      summary.kosten += +row.Kosten;
      summary.plankosten += +row.PlanKosten;
      return summary;
    },
    { kosten: 0, plankosten: 0 }
  );

  return {
    ...state,
    summary: {
      kosten: kosten.toFixed(2),
      plankosten: plankosten.toFixed(2),
    },
  };
};

function kostenAllRows(state: KostenSummary) {
  return [...state.rows.personal, ...state.rows.material, ...state.rows.maschine, ...state.rows.fremdleistung];
}
